import { z } from 'zod';

// ── Common validations ──────────────────────────────────────────────
const emailSchema = z
  .string()
  .min(1, 'Please enter your email address')
  .email('Please enter a valid email address');

const futureDate = z
  .string()
  .min(1, 'Please select a date')
  .refine(
    (date) => new Date(date) >= new Date(new Date().setHours(0, 0, 0, 0)),
    { message: 'Please select today or a future date' }
  );

// ── Department options ───────────────────────────────────────────────
export const departmentOptions = [
  'Accounting',
  'Executive',
  'HR',
  'Integrations',
  'IT',
  'Marketing',
  'MST',
  'Operations',
  'Sales',
  'Warehouse',
] as const;

export type Department = (typeof departmentOptions)[number];

// ── Request type ────────────────────────────────────────────────────
export const requestTypes = [
  { value: 'collateral', label: 'Marketing Collateral', description: 'Design, creative, brochures, presentations' },
  { value: 'trade-show', label: 'Trade Show / Orders', description: 'Trade shows, desk orders, promo items' },
  { value: 'quick-note', label: 'Quick Note', description: 'Quick message to marketing' },
] as const;

export type RequestType = (typeof requestTypes)[number]['value'];

// ── Collateral "What do you need?" options ──────────────────────────
export const collateralNeedOptions = [
  'Video',
  'Edit to existing video',
  'Write A Blog',
  'Flyer',
  'Landing Page',
  'Website Update',
  'Write Something',
  'Flash Class',
  'PowerPoint Presentation',
  'CPK / Product Data Export',
  'Case Study',
  'Press Release',
  'Provide An image',
  'Create An Image',
  'Email Request',
  'Other',
] as const;

// ── Shared base fields ──────────────────────────────────────────────
const sharedFields = {
  name: z.string().min(2, 'Please enter your name'),
  email: emailSchema,
  department: z.string().min(1, 'Please select your department'),
  needByDate: futureDate,
};

// ── Collateral-specific schema ──────────────────────────────────────
const collateralFields = z.object({
  requestType: z.literal('collateral'),
  ...sharedFields,
  whatDoYouNeed: z
    .array(z.string())
    .min(1, 'Please select at least one option'),
  projectName: z.string().min(2, 'Please enter a project name'),
  goal: z
    .string()
    .min(10, 'Please describe the goal of this request'),
  audienceAndTopic: z
    .string()
    .min(10, 'Please describe the audience and topic in more detail'),
  description: z
    .string()
    .min(20, 'Please provide more detail so we can help you effectively'),
  wordCount: z.string().optional(),
  imageSizeDimensions: z.string().optional(),
  fileTypeNeeded: z
    .string()
    .min(2, 'Please specify the file type you need (e.g. PDF, PNG, DOCX)'),
  otherDescription: z.string().optional(),
  videoOrDropboxLink: z.string().optional(),
}).superRefine((data, ctx) => {
  if (
    data.whatDoYouNeed.includes('Edit to existing video') &&
    !data.videoOrDropboxLink?.trim()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please provide a Dropbox or video link for the existing video',
      path: ['videoOrDropboxLink'],
    });
  }
});

// ── Trade Show / Orders-specific schema ─────────────────────────────
const tradeShowFields = z.object({
  requestType: z.literal('trade-show'),
  ...sharedFields,
  titleOfRequest: z.string().min(2, 'Please enter a title for this request'),
  whoIsRequestFor: z
    .string()
    .min(5, 'Please indicate who this request is for'),
  whatDoYouNeedText: z
    .string()
    .min(20, 'Please be specific about what you need, including quantities and part numbers'),
  address: z.string().optional(),
  tradeShowDemoDetails: z.string().optional(),
  reasonForRequest: z.string().optional(),
});

// ── Quick Note schema ───────────────────────────────────────────────
const quickNoteFields = z.object({
  requestType: z.literal('quick-note'),
  ...sharedFields,
  subject: z.string().min(1, 'Please enter a subject'),
  message: z
    .string()
    .min(10, 'Please provide a bit more detail so we can help'),
});

// ── Discriminated union ─────────────────────────────────────────────
export const marketingFormSchema = z.discriminatedUnion('requestType', [
  collateralFields,
  tradeShowFields,
  quickNoteFields,
]);

export type MarketingFormValues = z.infer<typeof marketingFormSchema>;
export type CollateralFormValues = z.infer<typeof collateralFields>;
export type TradeShowFormValues = z.infer<typeof tradeShowFields>;
export type QuickNoteFormValues = z.infer<typeof quickNoteFields>;

// ── Ship Request schema ─────────────────────────────────────────────
// maxLength values match the RequestToShip_Data MSSQL column sizes.
// Keep these in sync if columns are altered in the DB.
export const shipRequestSchema = z.object({
  // Recipient (maps to: Name, Company, Address1-2, City, State, ZipCode, Country, Email2, PhoneNumber, Attention)
  shipToName:     z.string().min(2, 'Please enter the recipient name').max(100, 'Name must be 100 characters or fewer'),
  company:        z.string().max(100, 'Company name must be 100 characters or fewer').optional(),
  // Form-level cap of 50 is intentionally stricter than the DB Address1 column (100).
  address1:       z.string().min(5, 'Please enter the street address').max(50, 'Address line 1 must be 50 characters or fewer'),
  // Form-level cap of 50 is intentionally stricter than the DB Address2 column (100).
  address2:       z.string().max(50, 'Address line 2 must be 50 characters or fewer').optional(),
  city:           z.string().min(2, 'Please enter the city').max(60, 'City must be 60 characters or fewer'),
  state:          z.string().min(1, 'Please enter the state / province').max(50, 'State / province must be 50 characters or fewer'),
  zipCode:        z.string().min(3, 'Please enter the zip / postal code').max(20, 'Zip / postal code must be 20 characters or fewer'),
  country:        z.string().min(2, 'Please select a country'),
  customerEmail:  z.string().email('Please enter a valid email').optional().or(z.literal('')), // → Email2
  phoneNumber:    z.string().max(30, 'Phone number must be 30 characters or fewer').optional(),
  attention:      z.string().max(100, 'Attention field must be 100 characters or fewer').optional(),

  // Sender (maps to: email [Star], SentBy [email without domain], Department)
  senderEmail:    emailSchema,  // → email (auto-populated from OL)
  department:     z.string().min(1, 'Please select your department'),

  // Shipping details
  carrier: z.enum(['UPS', 'Federal Express', 'DHL', 'Other(Specify)'], {
    message: 'Please select a carrier',
  }),
  carrierOther: z.string().optional(),
  priority: z.enum(['Next Day Air', '2nd Day Air', 'Ground'], {
    message: 'Please select a shipping priority',
  }),
  shippingCharges: z.enum(['Prepaid', 'Freight Collect'], {
    message: 'Please select shipping charges type',
  }),
  upsAccount:       z.string().optional(),
  insuranceAmt:     z.coerce.number().min(0).optional(),
  po:               z.string().optional(),
  shipmentContains: z.enum(['Samples', 'Consignment', 'Literature', 'Other(specify)']).optional(),
  shipmentContainsOther: z.string().optional(),

  // Notes / dates
  notes:            z.string().optional(),
  requestedShipDate: z.string().optional(),
  needReturnSlip:   z.boolean().optional(),
  needsToArriveBy:  z.string().optional(),
});

export type ShipRequestFormValues = z.infer<typeof shipRequestSchema>;

// ── Ship request dropdown options ───────────────────────────────────
export const carrierOptions = ['UPS', 'Federal Express', 'DHL', 'Other(Specify)'] as const;
export const priorityOptions = ['Next Day Air', '2nd Day Air', 'Ground'] as const;
export const shippingChargesOptions = ['Prepaid', 'Freight Collect'] as const;
export const shipmentContainsOptions = ['Samples', 'Consignment', 'Literature', 'Other(specify)'] as const;

// ── IT Support form options ─────────────────────────────────────────
// Selecting this one doesn't submit a ticket — the form swaps to a website
// picker that opens the chosen site with the Page Feedback annotation widget
// enabled (see WebsiteIssueList / ITSupportForm).
export const websiteIssueRequestType = 'Star Micronics Websites Issue';

export const itRequestTypeOptions = [
  'Dropbox (Syncing Issue, Missing Files, etc)',
  'Hubspot (Contacts, Syncing, Lists)',
  'Microsoft 365 (Outlook, Excel, PPT, Power BI, etc)',
  'Network (Star01, Star09, Star08, etc)',
  'SAP & Server (Startrm13)',
  'OneLogin (Locked Out, Password, Adding new App, etc)',
  'PC Issue (Blue Screen, No Power, VPN, etc)',
  'Printer (Toshiba, HP, Brother)',
  'Salesforce (Report, Empower, etc)',
  'Signature Change (Name, Title, Phone, etc)',
  'Teams (Chat, Phone)',
  'Video Room',
  websiteIssueRequestType,
  'Other',
] as const;

export type ITRequestType = (typeof itRequestTypeOptions)[number];

export const itSupportFormSchema = z.object({
  email: emailSchema,
  department: z.string().min(1, 'Please select your department'),
  requestType: z.string().min(1, 'Please select a request type'),
  subject: z.string().min(2, 'Please enter a subject'),
  description: z.string().min(1, 'Please enter a description'),
});

export type ITSupportFormValues = z.infer<typeof itSupportFormSchema>;

// ── Legacy exports (kept for backward compat during transition) ─────
export const collateralFormSchema = collateralFields;
export const requestsFormSchema = tradeShowFields;
export type RequestsFormValues = TradeShowFormValues;
